import React from "react";
import {
  Card,
  Statistic,
  Progress,
  Badge,
  Tooltip,
  Space,
  Typography,
} from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Text } = Typography;

const SequenceStatusCard = ({ sequence }) => {
  const getStatusColor = (type) => {
    switch (type) {
      case "onboarding":
        return "#1890ff";
      case "engagement":
        return "#52c41a";
      case "retention":
        return "#faad14";
      case "inactivity":
        return "#f5222d";
      case "milestone":
        return "#722ed1";
      default:
        return "#d9d9d9";
    }
  };

  const getStatusIcon = () => {
    if (sequence.activeUsers > 0) {
      return <SyncOutlined spin style={{ color: "#52c41a" }} />;
    }
    return <PauseCircleOutlined style={{ color: "#d9d9d9" }} />;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.format("MMM DD, YYYY") : "N/A";
  };

  return (
    <Card
      style={{
        borderRadius: "12px",
        border: `2px solid ${getStatusColor(sequence.type)}`,
        marginBottom: "16px",
        transition: "all 0.3s ease",
      }}
      bodyStyle={{ padding: "20px" }}
      hoverable
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <div>
            <Text strong style={{ fontSize: "16px", color: "#262626" }}>
              {sequence.name}
            </Text>
            <br />
            <Badge
              color={getStatusColor(sequence.type)}
              text={
                sequence.type.charAt(0).toUpperCase() + sequence.type.slice(1)
              }
              style={{ marginTop: "4px" }}
            />
          </div>
          <div style={{ textAlign: "center" }}>
            {getStatusIcon()}
            <br />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {sequence.activeUsers > 0 ? "Active" : "Idle"}
            </Text>
          </div>
        </div>

        {/* Statistics Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "16px",
            marginBottom: "12px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Statistic
              title={
                <Tooltip title="Users currently active in this sequence">
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    <UserOutlined /> Active
                  </span>
                </Tooltip>
              }
              value={sequence.activeUsers || 0}
              valueStyle={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1890ff",
              }}
            />
          </div>

          <div style={{ textAlign: "center" }}>
            <Statistic
              title={
                <Tooltip title="Total users who have completed this sequence">
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    <CheckCircleOutlined /> Completed
                  </span>
                </Tooltip>
              }
              value={sequence.completedUsers || 0}
              valueStyle={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#52c41a",
              }}
            />
          </div>

          <div style={{ textAlign: "center" }}>
            <Statistic
              title={
                <Tooltip title="Total users who have entered this sequence">
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Total
                  </span>
                </Tooltip>
              }
              value={sequence.totalUsers || 0}
              valueStyle={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#595959",
              }}
            />
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginBottom: "8px" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Completion Rate
          </Text>
          <Progress
            percent={Math.round(sequence.completionRate || 0)}
            strokeColor={{
              "0%": getStatusColor(sequence.type),
              "100%": "#87d068",
            }}
            size="small"
            showInfo={true}
            format={(percent) => `${percent}%`}
            style={{ marginTop: "4px" }}
          />
        </div>

        {/* Footer Info */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "8px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Text type="secondary" style={{ fontSize: "11px" }}>
            <CalendarOutlined /> Created {formatDate(sequence.createdAt)}
          </Text>

          {sequence.activeUsers > 0 && (
            <Tooltip
              title={`${sequence.activeUsers} users are actively receiving emails from this sequence`}
            >
              <Badge
                count={sequence.activeUsers}
                style={{ backgroundColor: "#52c41a" }}
                size="small"
              />
            </Tooltip>
          )}
        </div>
      </Space>
    </Card>
  );
};

export default SequenceStatusCard;
